import { useMemo } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { ReactSortable } from "react-sortablejs";
import { PlusIcon } from "@heroicons/react/outline";
import { MdDeleteOutline, MdDragIndicator } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../common/components/Input";
import { Tooltip } from "../../../../common/components/Tooltip/Tooltip";

// content-quizz-sort stores answers: [{ text, index, id_internal }] where
//   • text        — the answer label (per-language, translatable PLD)
//   • index       — the CORRECT sort position (1-based); the "answer"
//   • id_internal — an immutable, stable identity assigned once (max+1 for new
//                   rows, never renumbered, gaps tolerated). The stats view
//                   (QuizSortDetail.tsx) correlates user placements to answers
//                   BY id_internal, so it must travel with its row untouched.
//
// This bespoke editor presents the answers as a drag-to-reorder list whose
// top-to-bottom order IS the correct order: dragging a row recomputes every
// row's `index` from its visual position, while `id_internal` and `text` stay
// glued to the row. `index` is therefore never a raw input.
//
// Storage split (mirrors content-quizz-image-multiple):
//   • base content object → answers: [{ text, index, id_internal }] (structural
//     source of truth; index + id_internal are shared across all languages,
//     .text holds the source-language text)
//   • per-language PLD     → list_answer_quizz_sort_data: [{ text, id_internal }]
//     (the translatable projection — `index` is dropped; canonical shape per
//     ghostJsonFields.js). NodeForm seeds RHF by merging the content structure
//     with the active language's PLD text (matched by id_internal), and
//     buildUpdatedProjectLanguageData projects back to { text, id_internal } on
//     the PLD write so it matches the backend afterUpdate sync.
// See docs/plans/partial/node-form-quizz-json-editors.md §3.3.

export type SortAnswer = {
  text: string;
  index: number;
  id_internal: number;
};

// Row carries a stable `id` for ReactSortable / React keys (id_internal is the
// natural, unique-per-answer choice). It is stripped before writing back.
type SortRow = SortAnswer & { id: number | string };

// minItems from the slopify jsonSchema (answersWithMin); soft max from the
// documented generation range ("Emit 3-5 items"). Existing data with more rows
// is preserved — we only stop the user from adding past the cap.
const MIN_ANSWERS = 3;
const MAX_ANSWERS = 5;

interface QuizSortAnswersFieldProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  label: string;
  /** View mode / mid-generation — fully read-only. */
  disabled?: boolean;
  /** Structural edits (add/remove/reorder) are source-language only. */
  isSourceLanguage: boolean;
  /** Optional per-answer character cap (none in the current schema). */
  maxLength?: number;
}

const QuizSortAnswersField = ({
  control,
  setValue,
  label,
  disabled = false,
  isSourceLanguage,
  maxLength,
}: QuizSortAnswersFieldProps) => {
  const { t } = useTranslation();

  const watched = useWatch({ control, name: "answers" });

  // RHF is the single source of truth. Derive the display rows from it, sorted
  // by `index` so the list opens in the current correct order (top = position
  // 1). Assign a stable `id` from id_internal for ReactSortable / React keys.
  const rows: SortRow[] = useMemo(() => {
    const arr: SortAnswer[] = Array.isArray(watched) ? watched : [];
    return arr
      .map((a, i) => ({
        text: a?.text ?? "",
        index: typeof a?.index === "number" ? a.index : i + 1,
        id_internal: typeof a?.id_internal === "number" ? a.id_internal : i,
        id: typeof a?.id_internal === "number" ? a.id_internal : `row-${i}`,
      }))
      .sort((a, b) => a.index - b.index);
  }, [watched]);

  // Text is editable in any language; structure (order, count) is locked
  // outside the source language.
  const structureLocked = disabled || !isSourceLanguage;

  // Central write: renumber `index` from each row's visual position (1-based,
  // contiguous), strip the transient `id`, and push the canonical
  // { text, index, id_internal } objects back to RHF in display order.
  const commit = (nextRows: Array<Partial<SortRow>>) => {
    const next: SortAnswer[] = nextRows.map((r, i) => ({
      text: r.text ?? "",
      index: i + 1,
      id_internal: typeof r.id_internal === "number" ? r.id_internal : i,
    }));
    setValue("answers", next, { shouldDirty: true });
  };

  const handleSetList = (nextRows: SortRow[]) => {
    // ReactSortable also calls setList on internal normalization (same order) —
    // ignore those to avoid a redundant dirtying write; only commit real drags.
    const sameOrder =
      nextRows.length === rows.length &&
      nextRows.every((r, i) => String(r.id) === String(rows[i].id));
    if (sameOrder) return;
    commit(nextRows);
  };

  const handleTextChange = (id: SortRow["id"], value: string) => {
    commit(rows.map((r) => (r.id === id ? { ...r, text: value } : r)));
  };

  const handleAdd = () => {
    // New rows get id_internal = max + 1 (stable, never reused), appended at the
    // bottom so they take the last correct position. Author types the text and
    // drags it into place.
    const maxId = rows.reduce(
      (m, r) => (typeof r.id_internal === "number" ? Math.max(m, r.id_internal) : m),
      -1,
    );
    commit([...rows, { text: "", id_internal: maxId + 1 }]);
  };

  const handleRemove = (id: SortRow["id"]) => {
    commit(rows.filter((r) => r.id !== id));
  };

  const canAdd = !structureLocked && rows.length < MAX_ANSWERS;
  const canRemove = !structureLocked && rows.length > MIN_ANSWERS;

  // A blank sort item renders as an empty draggable in VR — unplayable. Block
  // the save (see quizzSortMissingText in NodeForm) and surface it here. Text
  // is editable in every language, so this gate is language-agnostic (an empty
  // translation is just as broken as an empty source).
  const hasEmptyAnswer = !disabled && rows.some((r) => !(r.text ?? "").trim());

  // Surface WHY the add/remove buttons disable at the min/max — a silently
  // greyed button reads as a bug. Grey info, shown only where structure is
  // editable (source language, edit mode) and nothing more pressing (the
  // blocking empty-text error) needs the single reserved hint line.
  const atMinAnswers = rows.length <= MIN_ANSWERS;
  const atMaxAnswers = rows.length >= MAX_ANSWERS;
  const showMinAnswersHint = !disabled && isSourceLanguage && atMinAnswers && !hasEmptyAnswer;
  const showMaxAnswersHint =
    !disabled && isSourceLanguage && atMaxAnswers && !atMinAnswers && !hasEmptyAnswer;
  const minAnswersHint = t("pages.project.editNode.quizzSortMinAnswersHint") as string;
  const maxAnswersHint = t("pages.project.editNode.quizzSortMaxAnswersHint") as string;

  return (
    <div className="w-full mt-4">
      <div className="flex items-center justify-between mb-2 gap-2">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        <Tooltip
          message={!canAdd && !structureLocked ? maxAnswersHint : ""}
          className="shrink-0"
          classNameChildren="right-0"
        >
          <button
            type="button"
            onClick={handleAdd}
            disabled={!canAdd}
            className="btn-alternative-outline flex items-center gap-1.5 text-xs px-2 py-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            <span>{t("pages.project.editNode.quizzAddAnswer")}</span>
          </button>
        </Tooltip>
      </div>

      {!structureLocked ? (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzSortReorderHint")}
        </p>
      ) : !disabled ? (
        <p className="mb-3 text-xs italic text-gray-500">
          {t("pages.project.editNode.quizzStructureLockedHint")}
        </p>
      ) : null}

      {/* Single reserved hint line (avoids layout shift). Priority: the blocking
          empty-text error (red), then the informational min/max-items note
          (grey) explaining why the remove / add buttons disable. */}
      <p
        className={`min-h-[1.25rem] mb-2 text-xs font-medium ${
          showMinAnswersHint || showMaxAnswersHint ? "text-gray-500 italic" : "text-red-600"
        }`}
      >
        {hasEmptyAnswer
          ? t("pages.project.editNode.quizzSortNeedsText")
          : showMinAnswersHint
          ? minAnswersHint
          : showMaxAnswersHint
          ? maxAnswersHint
          : ""}
      </p>

      <ReactSortable
        list={rows}
        setList={handleSetList}
        handle=".quizz-sort-drag-handle"
        disabled={structureLocked}
        animation={150}
        className="space-y-2"
      >
        {rows.map((row, position) => (
          <div
            key={row.id}
            className="border border-gray-200 rounded-lg p-3 flex items-center gap-3 bg-white"
          >
            <Tooltip
              message={
                structureLocked ? "" : (t("pages.project.editNode.quizzSortDragHandle") as string)
              }
              className="shrink-0"
            >
              <span
                className={`quizz-sort-drag-handle flex items-center text-gray-400 ${
                  structureLocked ? "cursor-not-allowed opacity-40" : "cursor-grab"
                }`}
                aria-hidden="true"
              >
                <MdDragIndicator size={20} />
              </span>
            </Tooltip>

            {/* Correct-position badge — reflects the row's drag order. */}
            <span className="shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-gray-100 text-gray-700 text-sm font-semibold">
              {position + 1}
            </span>

            <div className="flex-1 min-w-0">
              <Input
                type="text"
                value={row.text ?? ""}
                onChange={(e) => handleTextChange(row.id, e.target.value)}
                disabled={disabled}
                maxLength={maxLength}
                placeholder={t("pages.project.editNode.quizzAnswerPlaceholder") as string}
                className="dynamic-field"
              />
            </div>

            <Tooltip
              message={!canRemove && !structureLocked ? minAnswersHint : ""}
              className="shrink-0"
              classNameChildren="right-0"
            >
              <button
                type="button"
                onClick={() => handleRemove(row.id)}
                disabled={!canRemove}
                className="text-red-500 p-1 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label={t("general.delete") as string}
              >
                <MdDeleteOutline size={20} />
              </button>
            </Tooltip>
          </div>
        ))}
      </ReactSortable>
    </div>
  );
};

export default QuizSortAnswersField;
